import type { AudioEngine } from './audio-engine'
import type { DocWithContent } from '@/models/interfaces'
import { assertNotNull } from '@/helpers/typing'
import { chapterTimings, findChapterAtPosition } from './chapter-timing'

/**
 * A structure to hold the chapter data.
 */
export class ChapterData {
  constructor(
    public idx: number,
    public title: string,
    public url_slug: string,
    public start: number,
    public end: number,
  ) {}
}

/**
 * The list of audio chapters.
 *
 * We have one audio file per book and timings metadata with
 * start time for each chapter.
 * This class parses the metadata into an array of `ChapterData`
 * objects and keeps track of the current audio position to
 * find the appropriate book chapter.
 *
 * It does not hook to audio events directly, the `update` method
 * is called by ChapterPrevNextControl when audio time changes.
 */
class Chapters {
  private _doc: DocWithContent
  private _chapters: ChapterData[] = []
  private _currentChapterIdx: number

  constructor(doc: DocWithContent, currentChapterIdx?: number) {
    this._doc = doc
    this._currentChapterIdx = currentChapterIdx ?? 0

    // Go over pages, build the `_chapters` array with chapter timings.
    const pages = doc.content
    const timings = chapterTimings(doc)

    for (let idx = 0; idx < pages.length; idx++) {
      const chapter = pages[idx]
      const timing = timings[idx]
      const chapterData = {
        idx: idx,
        title: chapter.title,
        url_slug: chapter.url_slug,
        start: timing.start,
        end: timing.end ?? Infinity,
      }

      if (
        currentChapterIdx === undefined &&
        chapter.url_slug === doc.user.position_chapter
      ) {
        this._currentChapterIdx = idx
      }

      this._chapters.push(chapterData)
    }
  }

  /**
   * Current chapter data.
   */
  current(): ChapterData {
    return this._chapters[this._currentChapterIdx]
  }

  /**
   * Null chapter, returned when there is no next/prev chapter.
   */
  nullChapter(): ChapterData {
    return {
      idx: 0,
      start: 0,
      end: 0,
      title: '',
      url_slug: '',
    }
  }

  /**
   * Next chapter data.
   */
  next(): ChapterData {
    if (this._currentChapterIdx < this._chapters.length - 1) {
      return this._chapters[this._currentChapterIdx + 1]
    }
    return this.nullChapter()
  }

  /**
   * Prev chapter data.
   */
  prev(): ChapterData {
    if (this._currentChapterIdx > 0) {
      return this._chapters[this._currentChapterIdx - 1]
    }
    return this.nullChapter()
  }

  /**
   * Do we have the next chapter?
   */
  hasNext(): boolean {
    return this.next().url_slug !== ''
  }

  /**
   * Do we have the prev chapter?
   */
  hasPrev(): boolean {
    return this.prev().url_slug !== ''
  }

  /**
   * Switch to the next chapter.
   */
  goNext(): void {
    if (!this.hasNext()) {
      return
    }
    this._currentChapterIdx += 1
  }

  /**
   * Switch to the previous chapter.
   */
  goPrev(): void {
    if (!this.hasPrev()) {
      return
    }
    this._currentChapterIdx -= 1
  }

  /**
   * Audio playback update handler.
   *
   * Sets current chapter accordingly to the audio position.
   */
  go(chapterIndex: number): ChapterData {
    this._currentChapterIdx = chapterIndex
    return this.current()
  }

  /**
   * Update current chapter based on audio playback time.
   *
   * The method is called by ChapterPrevNextControl in the `timeupdate`
   * audio event.
   */
  update(newTime: number): ChapterData | null {
    // The newTime matches current chapter, no update needed.
    if (newTime >= this.current().start && newTime < this.current().end) {
      return null
    }

    // Set current chapter according to the audio time.
    const chapter = findChapterAtPosition(this._chapters, newTime)
    if (chapter && newTime < chapter.end) {
      this._currentChapterIdx = chapter.idx
      return chapter
    }

    // We should never be here unless it's a bug.
    /* eslint-disable no-console */
    // console.log(this._chapters.map((el) => JSON.stringify(el)))
    /* eslint-enable no-console */
    throw new Error(
      'Can not find chapter for time: ' +
        newTime +
        ' timings: ' +
        assertNotNull(this._doc.audio).timings,
    )
  }
}

export type AudioEventName =
  | 'intentchange'
  | 'loadstart'
  | 'progress'
  | 'suspend'
  | 'abort'
  | 'error'
  | 'emptied'
  | 'stalled'
  | 'loadedmetadata'
  | 'loadeddata'
  | 'canplay'
  | 'canplaythrough'
  | 'playing'
  | 'waiting'
  | 'seeking'
  | 'seeked'
  | 'ended'
  | 'durationchange'
  | 'timeupdate'
  | 'play'
  | 'pause'
  | 'ratechange'
  | 'resize'
  | 'volumechange'

export type EventListener = (evt: Event) => void

/**
 * AudioDoc - audio engine and doc wrapper.
 */
export class AudioDoc {
  private _doc: DocWithContent
  private _chapters: Chapters

  private _audio: AudioEngine
  private _canplay = false
  private _requestedCurrentTime: number
  private _canplayListener: () => void
  private _listeners: {
    eventName: AudioEventName
    eventListener: EventListener
  }[] = []
  private _beforeSeekListeners: (() => void)[] = []

  // We set it to true once we attempt to add the audio file to cache.
  private _cached: boolean = false

  constructor(
    audio: AudioEngine,
    doc: DocWithContent,
    currentChapterIdx?: number,
    initialTime: number = audio.currentTime,
  ) {
    this._doc = doc
    this._chapters = new Chapters(doc, currentChapterIdx)

    this._audio = audio
    this._requestedCurrentTime = initialTime
    this._canplayListener = (): void => {
      if (!this._canplay) {
        this._canplay = true
        // Run the position restore only after we get the "canplay" event.
        // On mobile Safari, setting the audio.currentTime before that
        // does not work.
        if (!this._audio.playBeforeCanPlay) {
          this._audio.currentTime = this._requestedCurrentTime
        }
      }
    }
    this._audio.addEventListener('canplay', this._canplayListener)
  }

  get doc(): DocWithContent {
    return this._doc
  }

  // The session swaps the doc when another player takes it over, see
  // `PlayerSession.takeOver`.
  set doc(value: DocWithContent) {
    this._doc = value
  }

  get chapters(): Chapters {
    return this._chapters
  }

  get currentTime(): number {
    return this._audio.currentTime
  }

  set currentTime(value: number) {
    this._beforeSeekListeners.forEach((listener) => listener())
    this._requestedCurrentTime = value
    this._audio.currentTime = value
  }

  get playbackRate(): number {
    return this._audio.playbackRate
  }

  set playbackRate(value: number) {
    this._audio.playbackRate = value
  }

  get volume(): number {
    return this._audio.volume
  }

  set volume(value: number) {
    this._audio.volume = value
  }

  get duration(): number {
    return this._audio.duration
  }

  get paused(): boolean {
    return this._audio.paused
  }

  get ended(): boolean {
    return this._audio.ended
  }

  async play(): Promise<void> {
    if (this._audio.cacheSourceOnPlay !== false) {
      this._cacheAudio()
    }
    if (!this._canplay && !this._audio.playBeforeCanPlay) {
      this._audio.load()
      return
    }

    this.rewindIfEnded()

    // Play is asynchronous. Calling pause() before it settles rejects the play
    // promise with an AbortError:
    // https://html.spec.whatwg.org/multipage/media.html#internal-pause-steps
    await this._audio.play()
  }

  private rewindIfEnded(): void {
    const duration = this.duration
    if (
      Number.isFinite(duration) &&
      duration > 0 &&
      this.currentTime >= duration
    ) {
      this.currentTime = this.chapters.current().start
    }
  }

  async pause(): Promise<void> {
    await this._audio.pause()
  }

  load(): void {
    this._audio.load()
  }

  /**
   * Add event listener to the audio engine.
   * Store all event listeners so that we can remove them.
   * For reference see: https://stackoverflow.com/questions/19469881/remove-all-event-listeners-of-specific-type
   */
  addListener(eventName: AudioEventName, eventListener: EventListener): void {
    this._audio.addEventListener(eventName, eventListener)
    this._listeners.push({ eventName, eventListener })
  }

  addBeforeSeekListener(listener: () => void): void {
    this._beforeSeekListeners.push(listener)
  }

  /**
   * Remove all attached listeners.
   */
  removeListeners(): void {
    this._listeners.forEach(({ eventName, eventListener }) =>
      this._audio.removeEventListener(eventName, eventListener),
    )
    this._listeners = []
    this._beforeSeekListeners = []
    this._audio.removeEventListener('canplay', this._canplayListener)
  }

  // Cache audio for offline usage.
  async _cacheAudio(): Promise<void> {
    // Exit if we have added file to cache already.
    if (this._cached) {
      return
    }
    // Exit if window.caches is not available (IE).
    if (!window.caches) {
      return
    }
    try {
      // Preemptively mark this as cached to prevent further calls to this
      // while the audio is actually being cached.
      this._cached = true
      const cache = await caches.open('shortform-audio')
      await cache.add(this._audio.src)
    } catch {
      this._cached = false
    }
  }
}
